import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fetchEmpAllocation } from "../../actions/EmpAllocationAction";

/* ------------ Styles ------------ */

const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "650px",
  overflowY: "auto",
  overflowX: "auto",
  position: "relative",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    minWidth: "100%",
  },
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
    maxHeight: "calc(100vh - 380px)",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "16px 12px",
  fontSize: "15px",
  textAlign: "center",
  whiteSpace: "nowrap",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    fontSize: "15px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 8px",
    fontSize: "13px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.5",
    "&.MuiTableCell-head": {
      fontSize: "13px",
      padding: "12px 8px",
      lineHeight: "1.4",
    },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.09)",
    cursor: "pointer",
  },
}));

export default function EmployeeAllocationMain() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.empAllocation);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("allocation");

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  useEffect(() => {
    const today = new Date();
    setSelectedDate(today);
    const formattedToday = formatDate(today);
    dispatch(fetchEmpAllocation(formattedToday));
  }, [dispatch]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    const formatted = formatDate(date);
    dispatch(fetchEmpAllocation(formatted));
  };

  const handleViewModeChange = (event) => {
    setViewMode(event.target.value);
  };

  const filteredData =
    data?.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    ) || [];

  // Group by Task for allocation view
  const groupedByTask = filteredData.reduce((acc, item) => {
    const task = item.CDLjob;
    if (!acc[task]) {
      acc[task] = {
        CDLjob: task,
        countIWOno: 0,
      };
    }
    acc[task].countIWOno += Number(item.countIWOno || 0);
    return acc;
  }, {});

  // Group by Supervisor
const groupedBySupervisor = filteredData.reduce((acc, item) => {
  const supervisor = item.Supervisorname;

  if (!acc[supervisor]) {
    acc[supervisor] = {
      Supervisorname: supervisor,
      LocCode: item.LocCode,
      countIWOno: 0,
    };
  }

  acc[supervisor].countIWOno += Number(item.countIWOno || 0);
  return acc;
}, {});


  const taskArray = Object.values(groupedByTask);
  const supervisorArray = Object.values(groupedBySupervisor);

  const totalEmployees =
    filteredData.reduce((sum, item) => sum + Number(item.countIWOno || 0), 0) || 0;

  // Determine current data based on view mode
  const getCurrentData = () => {
    if (viewMode === "allocation") return taskArray;
    if (viewMode === "supervisor") return supervisorArray;
    return filteredData;
  };

  const currentData = getCurrentData();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#0e3c66",
            textAlign: "center",
            mb: 2,
            letterSpacing: "1px",
          }}
        >
          Employees Allocation
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Date Select Button */}
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              startIcon={<CalendarTodayIcon />}
              onClick={() => setOpenDatePicker(true)}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#142c54",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "16px",
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
            >
              {formatDate(selectedDate)}
            </Button>
          </Grid>

          {/* View Mode Dropdown */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: "#154C79",
                }}
              >
                View Type
              </InputLabel>
              <Select
                value={viewMode}
                onChange={handleViewModeChange}
                label="View Type"
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  borderRadius: "20px",
                  backgroundColor: "#F0F4F8",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  height: "56px",
                  "& .MuiSelect-icon": {
                    color: "#154C79",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#154C79",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 250,
                      overflowY: "auto",
                    },
                  },
                }}
              >
                <MenuItem
                  value="allocation"
                  sx={{
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    padding: "8px 16px",
                    fontWeight: "400",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }}
                >
                  Summary - Employee Allocation
                </MenuItem>
                <MenuItem
                  value="supervisor"
                  sx={{
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    padding: "8px 16px",
                    fontWeight: "400",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }}
                >
                  Summary - Supervisor
                </MenuItem>
                <MenuItem
                  value="details"
                  sx={{
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    padding: "8px 16px",
                    fontWeight: "400",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                    },
                  }}
                >
                  Details
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={handleDateChange}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => (
              <TextField {...params} sx={{ display: "none" }} />
            )}
          />
        )}

        {/* Total Employee Count */}
        {!loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                background: "#e8f0fa",
                border: "1px solid #bbd0ea",
                padding: "8px 14px",
                borderRadius: "8px",
                textAlign: "center",
                fontWeight: 600,
                color: "#0E3C66",
              }}
            >
              Total Employees: {totalEmployees}
            </Box>
          </Box>
        )}

        {/* Search Field */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder={
              viewMode === "allocation"
                ? "Search CDL Jobs..."
                : viewMode === "supervisor"
                ? "Search Supervisors..."
                : "Search Details..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#154C79" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                height: "56px",
                fontSize: "16px",
              },
            }}
          />
        </Box>

        {/* Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress sx={{ color: "#154C79" }} />
          </Box>
        ) : (
          <StyledTableContainer component={Paper}>
            <Table sx={{ minWidth: { xs: "100%", sm: 500 } }}>
              <TableHead>
                <TableRow>
                  {viewMode === "allocation" && (
                    <>
                      <StyledTableCell sx={{ textAlign: "center" }}>
                        CDL JOBS
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "center" }}>
                        Employees
                      </StyledTableCell>
                    </>
                  )}
                  {viewMode === "supervisor" && (
                    <>
                      <StyledTableCell sx={{ textAlign: "left" }}>
                        Location
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>
                        Supervisor
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "center" }}>
                        Total Employees
                      </StyledTableCell>
                    </>
                  )}
                  {viewMode === "details" && (
                    <>
                      {/* <StyledTableCell>Job Category</StyledTableCell>
                      <StyledTableCell>Job Main</StyledTableCell> */}
                      <StyledTableCell sx={{ textAlign: "left" }}>
                        Description
                      </StyledTableCell>
                      <StyledTableCell>No. of Employees</StyledTableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {currentData.length > 0 ? (
                  currentData.map((row, index) => (
                    <StyledTableRow key={index}>
                      {viewMode === "allocation" && (
                        <>
                          <StyledTableCell sx={{ textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.CDLjob}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600 }}>
                            {row.countIWOno}
                          </StyledTableCell>
                        </>
                      )}
                      {viewMode === "supervisor" && (
                        <>
                          <StyledTableCell sx={{ textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.LocCode}
                          </StyledTableCell>
                          <StyledTableCell sx={{ textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.Supervisorname}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600 }}>
                            {row.countIWOno}
                          </StyledTableCell>
                        </>
                      )}
                      {viewMode === "details" && (
                        <>
                          {/* <StyledTableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.Emp_jCat}
                          </StyledTableCell>
                          <StyledTableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.Emp_jMain}
                          </StyledTableCell> */}
                          <StyledTableCell sx={{ textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.EngSpec}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600 }}>
                            {row.countIWOno}
                          </StyledTableCell>
                        </>
                      )}
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={viewMode === "details" ? 3 : viewMode === "supervisor" ? 3 : 2}
                      align="center"
                      sx={{ fontSize: "14px" }}
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}
      </PageContainer>
    </LocalizationProvider>
  );
}
// import React from "react";
// import { styled } from "@mui/material/styles";
// import { Box, Typography } from "@mui/material";
// import BuildIcon from "@mui/icons-material/Build";

// /* ------------ Styles ------------ */

// const PageContainer = styled(Box)(({ theme }) => ({
//   display: "flex",
//   flexDirection: "column",
//   fontFamily: "'Poppins', sans-serif",
//   backgroundColor: "#f5f5f5",
//   position: "relative",
//   overflow: "hidden",
// }));

// const HeaderSection = styled(Box)(({ theme }) => ({
//   position: "relative",
//   width: "100%",
//   height: "200px",
//   overflow: "hidden",
//   [theme.breakpoints.down("sm")]: {
//     height: "180px",
//   },
// }));

// const GeometricShape = styled(Box)(({ theme }) => ({
//   position: "absolute",
//   top: 0,
//   left: 0,
//   width: "100%",
//   height: "100%",
//   "& svg": {
//     width: "100%",
//     height: "auto",
//     display: "block",
//   },
// }));

// const MenuIcon = styled(Box)(({ theme }) => ({
//   position: "absolute",
//   top: "20px",
//   left: "20px",
//   zIndex: 10,
//   width: "40px",
//   height: "40px",
//   backgroundColor: "#F5A623",
//   borderRadius: "8px",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   cursor: "pointer",
//   "& div": {
//     width: "24px",
//     height: "2px",
//     backgroundColor: "#fff",
//     position: "relative",
//     "&::before, &::after": {
//       content: '""',
//       position: "absolute",
//       width: "24px",
//       height: "2px",
//       backgroundColor: "#fff",
//       left: 0,
//     },
//     "&::before": {
//       top: "-8px",
//     },
//     "&::after": {
//       bottom: "-8px",
//     },
//   },
//   [theme.breakpoints.down("sm")]: {
//     top: "15px",
//     left: "15px",
//     width: "36px",
//     height: "36px",
//   },
// }));

// const LogoContainer = styled(Box)(({ theme }) => ({
//   position: "absolute",
//   left: "50%",
//   transform: "translateX(-50%)",
//   zIndex: 10,
//   backgroundColor: "#fff",
//   padding: "15px 30px",
//   borderRadius: "8px",
//   boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
//   [theme.breakpoints.down("sm")]: {
//     padding: "12px 24px",
//   },
// }));

// const LogoText = styled(Box)(() => ({
//   display: "flex",
//   alignItems: "center",
//   gap: "8px",
// }));

// const ContentSection = styled(Box)(({ theme }) => ({
//   flex: 1,
//   display: "flex",
//   flexDirection: "column",
//   alignItems: "center",
//   justifyContent: "center",
//   padding: "40px 20px 60px",
//   marginTop: "30px",
//   [theme.breakpoints.down("sm")]: {
//     padding: "30px 15px 40px",
//     marginTop: "20px",
//   },
// }));

// const GreetingText = styled(Typography)(({ theme }) => ({
//   fontSize: "24px",
//   fontWeight: 600,
//   color: "#0e3c66",
//   marginBottom: "40px",
//   textAlign: "center",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "20px",
//     marginBottom: "30px",
//   },
// }));

// const MaintenanceCard = styled(Box)(({ theme }) => ({
//   backgroundColor: "#0e3c66",
//   borderRadius: "16px",
//   padding: "60px 40px",
//   maxWidth: "500px",
//   width: "100%",
//   textAlign: "center",
//   boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
//   [theme.breakpoints.down("sm")]: {
//     padding: "40px 24px",
//     borderRadius: "12px",
//   },
// }));

// const IconCircle = styled(Box)(({ theme }) => ({
//   width: "120px",
//   height: "120px",
//   backgroundColor: "#fff",
//   borderRadius: "50%",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   margin: "0 auto 30px",
//   [theme.breakpoints.down("sm")]: {
//     width: "100px",
//     height: "100px",
//     marginBottom: "24px",
//   },
// }));

// const StyledBuildIcon = styled(BuildIcon)(({ theme }) => ({
//   fontSize: "70px",
//   color: "#0e3c66",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "60px",
//   },
// }));

// const MaintenanceTitle = styled(Typography)(({ theme }) => ({
//   fontSize: "32px",
//   fontWeight: 700,
//   color: "#fff",
//   marginBottom: "20px",
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "24px",
//     marginBottom: "16px",
//   },
// }));

// const MaintenanceDescription = styled(Typography)(({ theme }) => ({
//   fontSize: "16px",
//   color: "#fff",
//   opacity: 0.9,
//   lineHeight: 1.6,
//   fontFamily: "'Poppins', sans-serif",
//   [theme.breakpoints.down("sm")]: {
//     fontSize: "14px",
//   },
// }));

// /* ------------ Component ------------ */

// export default function UnderMaintenance() {
//   return (
//     <PageContainer>

//       {/* Content */}
//       <ContentSection>

//         <MaintenanceCard>
//           <IconCircle>
//             <StyledBuildIcon />
//           </IconCircle>

//           <MaintenanceTitle>
//             Under Maintenance
//           </MaintenanceTitle>

//           <MaintenanceDescription>
//             We're currently performing scheduled maintenance to improve your experience. We'll be back shortly!
//           </MaintenanceDescription>
//         </MaintenanceCard>
//       </ContentSection>
//     </PageContainer>
//   );
// }